import type { CSSProperties } from 'react';

import type { TooltipProps } from '@mui/material';
import { Tooltip, Typography, styled, tooltipClasses } from '@mui/material';

export interface LookoTooltipProps {
  /**
   * 툴팁 텍스트.
   */
  title: React.ReactNode;

  /**
   * 툴팁 위치.
   *
   * @default bottom
   */
  placement?: TooltipProps['placement'];

  /**
   * 툴팁 최대 너비.
   * none 입력 시, no wrapping
   *
   * @default 300px
   */
  maxWidth?: CSSProperties['maxWidth'];

  /**
   * hover시 tooltip 띄우기 여부
   */
  disabled?: boolean;
}

/**
 * 툴팁. hover 시 `title` 내용 표시.
 *
 * 기본 사용:
 * ```tsx
 * <LookoTooltip title="설명 텍스트">
 *   <span><Button>버튼</Button></span>
 * </LookoTooltip>
 * ```
 *
 * 최대 너비 지정:
 * ```tsx
 * <LookoTooltip title="긴 설명" maxWidth={500}>...</LookoTooltip>
 * ```
 *
 * 줄바꿈 없음:
 * ```tsx
 * <LookoTooltip title="한 줄 텍스트" maxWidth="none">...</LookoTooltip>
 * ```
 *
 * @see https://www.figma.com/file/PFrAvDaThtf8qMSRc9pNaV/LDS(Looko-Design-System)?type=design&node-id=2867-3155&mode=design&t=FhzwuWRvVwdHHswE-4
 */
export const LookoTooltip = styled(
  ({
    className,
    maxWidth,
    ...props
  }: Pick<TooltipProps, 'className' | 'children' | 'arrow' | 'open'> & LookoTooltipProps) => {
    if (props.disabled) {
      return <>{props.children}</>;
    }

    return (
      <Tooltip
        {...props}
        classes={{ popper: className }}
        title={<Typography variant="Label_Looko">{props.title}</Typography>}
      />
    );
  },
)(({ ...props }) => {
  return {
    [`& .${tooltipClasses.tooltip}`]: {
      maxWidth: props.maxWidth,
    },
  };
});
